import { reactive } from 'vue'
import { DASHBOARD_TOKEN } from './env'
import { login, refreshAccessToken } from '@/api/auth'

/**
 * 大屏访问凭据（只读账号 dashboard_ro，见 docs/for_ops/data-dashboard-ops-20261002.md）。
 *
 * 两种进入方式：
 *  ① 直接粘贴 accessToken（临时/手动，兼容 P0 方案）；
 *  ② 输入 用户名 + 密码（可勾选「记住凭据」）—— 前端自己调 admin login，
 *     之后 accessToken 临近到期自动用 refreshToken 续期，refreshToken 过期
 *     则用保存的账号密码重新登录（无人值守，7×24 大屏不必人工换 token）。
 *
 * 存储：accessToken / refreshToken / 到期时间 / 账号密码**全部只进 localStorage**，
 * 不写进仓库、不进构建产物、不上传别处（账号密码仅勾选「记住凭据」时落盘）。
 */
export const TOKEN_STORAGE_KEY = 'datax_token'
export const REFRESH_STORAGE_KEY = 'datax_refresh_token'
export const EXPIRES_STORAGE_KEY = 'datax_expires_at'
export const CREDENTIALS_STORAGE_KEY = 'datax_credentials'

/** 到期前多久触发续期（accessToken 生产有效期 8 小时）。 */
export const RENEW_AHEAD_MS = 10 * 60 * 1000

export interface StoredCredentials {
  username: string
  password: string
  tenantId: number
}

function readStorage(key: string): string {
  try {
    return window.localStorage.getItem(key) || ''
  } catch (err) {
    // 隐私模式 / 禁用存储时降级为「本次会话内存态」
    return ''
  }
}

function writeStorage(key: string, value: string): void {
  try {
    window.localStorage.setItem(key, value)
  } catch (err) {
    // 写不进去也不影响本次会话（内存里仍有值）
  }
}

function removeStorage(key: string): void {
  try {
    window.localStorage.removeItem(key)
  } catch (err) {
    // ignore
  }
}

/** 允许直接粘贴 "Bearer xxx"，统一去掉前缀。 */
function normalize(raw: string): string {
  return (raw || '').trim().replace(/^bearer\s+/i, '')
}

/** 未勾选「记住凭据」时账号密码只留在内存（刷新页面即失效）。 */
let memoryCredentials: StoredCredentials | null = null

export function readCredentials(): StoredCredentials | null {
  if (memoryCredentials) return memoryCredentials
  const raw = readStorage(CREDENTIALS_STORAGE_KEY)
  if (!raw) return null
  try {
    const parsed = JSON.parse(raw) as StoredCredentials
    if (parsed && parsed.username && parsed.password) {
      return { username: parsed.username, password: parsed.password, tenantId: Number(parsed.tenantId) || 1 }
    }
  } catch (err) {
    // 坏 JSON 当作没有保存
  }
  return null
}

export function saveCredentials(credentials: StoredCredentials, remember: boolean): void {
  memoryCredentials = credentials
  if (remember) {
    writeStorage(CREDENTIALS_STORAGE_KEY, JSON.stringify(credentials))
  } else {
    removeStorage(CREDENTIALS_STORAGE_KEY)
  }
}

export function clearCredentials(): void {
  memoryCredentials = null
  removeStorage(CREDENTIALS_STORAGE_KEY)
}

function readRefreshToken(): string {
  return readStorage(REFRESH_STORAGE_KEY)
}

function readExpiresAt(): number {
  return Number(readStorage(EXPIRES_STORAGE_KEY)) || 0
}

/** 从 JWT payload 解出 exp（毫秒）；解不出返回 0（未知，交给 401 兜底）。 */
function jwtExpiresAt(token: string): number {
  const parts = (token || '').split('.')
  if (parts.length < 2) return 0
  try {
    const base64 = parts[1].replace(/-/g, '+').replace(/_/g, '/')
    const padded = base64 + '='.repeat((4 - (base64.length % 4)) % 4)
    const payload = JSON.parse(window.atob(padded)) as { exp?: number }
    return payload.exp ? payload.exp * 1000 : 0
  } catch (err) {
    return 0
  }
}

/** 凭据变化后刷新提示层显隐：有凭据可自动恢复时不弹输入框。 */
function syncPromptVisibility(notice = ''): void {
  const recoverable = !!readRefreshToken() || !!readCredentials()
  authState.promptVisible = !authState.token && !recoverable
  authState.notice = notice
}

const initialToken = normalize(readStorage(TOKEN_STORAGE_KEY)) || normalize(DASHBOARD_TOKEN)

export const authState = reactive({
  token: initialToken,
  /** 无凭据可恢复且无 token 时为 true，弹出输入浮层 */
  promptVisible: !initialToken && !readRefreshToken() && !readCredentials(),
  /** 浮层内的提示文案（如 token 失效） */
  notice: '',
  /** 登录 / 续期进行中（浮层按钮转圈用） */
  busy: false,
  /** 已保存账号密码 = 自动续期可用（浮层展示状态） */
  autoRenew: !!readCredentials(),
})

export function getToken(): string {
  return authState.token
}

/** 距到期是否已不足 RENEW_AHEAD_MS；到期时间未知（0）时返回 false。 */
export function needsRenewal(): boolean {
  const expiresAt = readExpiresAt() || jwtExpiresAt(authState.token)
  if (!expiresAt) return false
  return expiresAt - Date.now() < RENEW_AHEAD_MS
}

/** 记下新的 accessToken（续期/登录共用）：到期时间优先取响应值，否则解 JWT。 */
function applyAccessToken(token: string, expiresTime?: number): void {
  const normalized = normalize(token)
  authState.token = normalized
  const expiresAt = Number(expiresTime) || jwtExpiresAt(normalized)
  if (expiresAt) writeStorage(EXPIRES_STORAGE_KEY, String(expiresAt))
  else removeStorage(EXPIRES_STORAGE_KEY)
  writeStorage(TOKEN_STORAGE_KEY, normalized)
  authState.promptVisible = false
  authState.notice = ''
}

/** 保存用户粘贴的 token；返回是否有效（兼容旧的手动粘贴路径）。 */
export function saveToken(raw: string): boolean {
  const token = normalize(raw)
  if (!token) return false
  applyAccessToken(token)
  return true
}

/** 丢掉当前 accessToken（保留 refreshToken / 账号密码，供自动恢复）。 */
export function invalidateAccessToken(): void {
  authState.token = ''
  removeStorage(TOKEN_STORAGE_KEY)
  removeStorage(EXPIRES_STORAGE_KEY)
}

/** 401 时清凭据并重新弹输入框（按现有语义：保留可自动恢复的 refresh/账号密码）。 */
export function clearToken(notice = ''): void {
  invalidateAccessToken()
  syncPromptVisibility(notice)
}

/** 彻底清空（续期与重新登录都失败时）：含 refreshToken 与保存的账号密码。 */
export function forgetAll(notice = ''): void {
  invalidateAccessToken()
  removeStorage(REFRESH_STORAGE_KEY)
  clearCredentials()
  authState.promptVisible = true
  authState.notice = notice
}

export function hasRefreshToken(): boolean {
  return !!readRefreshToken()
}

/**
 * 用账号密码登录（浮层「用户名*密码」模式）。
 * `remember` = 勾选「记住凭据」→ 落 localStorage，否则只留内存。
 */
export async function loginWithPassword(
  username: string,
  password: string,
  remember: boolean,
  tenantId = 1
): Promise<void> {
  authState.busy = true
  try {
    const data = await login(username, password, tenantId)
    if (!data || !data.accessToken) throw new Error('登录响应缺少 accessToken')
    if (data.refreshToken) writeStorage(REFRESH_STORAGE_KEY, data.refreshToken)
    applyAccessToken(data.accessToken, data.expiresTime)
    saveCredentials({ username, password, tenantId: data.tenantId || tenantId }, remember)
    authState.autoRenew = !!readCredentials()
    authState.promptVisible = false
    authState.notice = ''
  } finally {
    authState.busy = false
  }
}

let inFlightRenew: Promise<RenewOutcome> | null = null

interface RenewOutcome {
  ok: boolean
  /** 凭据被服务端拒绝（401/403）——与网络/5xx 故障区分，决定要不要清凭据 */
  authFailed: boolean
}

function isAuthRejection(err: unknown): boolean {
  const status = (err as { status?: number })?.status
  return status === 401 || status === 403
}

/** 单飞续期：并发请求只触发一次 refresh / 重新登录。 */
export function renewSession(): Promise<boolean> {
  return renewSessionDetailed().then((outcome) => outcome.ok)
}

function renewSessionDetailed(): Promise<RenewOutcome> {
  if (!inFlightRenew) {
    inFlightRenew = doRenewSession().finally(() => {
      inFlightRenew = null
    })
  }
  return inFlightRenew
}

async function doRenewSession(): Promise<RenewOutcome> {
  authState.busy = true
  let authFailed = false
  try {
    // ① refreshToken 换新 accessToken（refreshToken 7 天有效，可反复使用）
    const refreshToken = readRefreshToken()
    if (refreshToken) {
      try {
        const data = await refreshAccessToken(refreshToken)
        if (data && data.accessToken) {
          applyAccessToken(data.accessToken)
          return { ok: true, authFailed: false }
        }
      } catch (err) {
        // refresh 过期/被撤销 → 丢弃，落到重新登录（网络故障也走这条，但不清凭据）
        if (isAuthRejection(err)) authFailed = true
        removeStorage(REFRESH_STORAGE_KEY)
      }
    }
    // ② 有保存的账号密码 → 自动重新登录（refreshToken 也过期时的长效兜底）
    const credentials = readCredentials()
    if (credentials) {
      try {
        await loginWithPassword(credentials.username, credentials.password, true, credentials.tenantId)
        return { ok: true, authFailed: false }
      } catch (err) {
        if (isAuthRejection(err)) authFailed = true
      }
    }
    return { ok: false, authFailed }
  } finally {
    authState.busy = false
  }
}

/**
 * 每个请求前调用：token 临近到期先续期；无 token 时尝试用 refresh/账号密码恢复。
 * 返回可用的 accessToken；无法恢复时抛出可读错误（并让浮层接管）。
 * 凭据被拒（401/403）→ 清空凭据重弹；网络/服务故障 → 保留凭据只弹层（不毁掉 kiosk 凭据）。
 */
export async function ensureAuth(): Promise<string> {
  if (authState.token && !needsRenewal()) return authState.token
  const outcome = await renewSessionDetailed()
  if (outcome.ok) return authState.token
  if (authState.token) return authState.token
  if (outcome.authFailed) {
    forgetAll('保存的凭据已被服务端拒绝，请重新输入 Token 或账号密码')
  } else {
    authState.promptVisible = true
    authState.notice = '自动续期失败（网络或服务异常），凭据已保留；可手动输入凭据或稍后自动重试'
  }
  throw new Error('缺少可用凭据，请输入 Token 或账号密码')
}
